import { useState, useEffect } from 'react';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BellIcon,
} from '@heroicons/react/24/solid';
import {
  CheckCircleIcon,
  CheckIcon,
  CheckBadgeIcon,
  AdjustmentsHorizontalIcon,
  EyeSlashIcon,
  EyeIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftIcon,
} from '@heroicons/react/16/solid';
import clsx from 'clsx';

const FilterBar = ({
  sortOrder,
  setSortOrder,
  statusFilter,
  setStatusFilter,
  hideReplies,
  setHideReplies,
  searchText,
  setSearchText,
  removal,
  setRemoval,
  autoMod,
  setAutoMod,
}: {
  sortOrder: string;
  setSortOrder: any;
  statusFilter: any;
  setStatusFilter: any;
  hideReplies: boolean;
  setHideReplies: any;
  searchText: string;
  setSearchText: any;
  removal: boolean;
  setRemoval: any;
  autoMod: boolean;
  setAutoMod: any;
}) => {
  const [localSearchText, setLocalSearchText] = useState(searchText);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(localSearchText);
    }, 400);
    return () => clearTimeout(handler);
  }, [localSearchText, setSearchText]);

  // Status icon based on filter
  const statusDefault = statusFilter === 'all';
  const statusIcon =
    statusFilter === '0' ? (
      <span className="relative inline-block">
        <ChatBubbleOvalLeftIcon
          className={clsx(
            'w-4 h-4',
            statusDefault ? 'text-gray-400' : 'text-white'
          )}
        />
        <span
          className={clsx(
            'absolute top-0 right-0 w-2 h-2 rounded-full',
            statusDefault ? 'bg-gray-400' : 'bg-red'
          )}
        />
      </span>
    ) : statusFilter === '1' ? (
      <span className="relative flex items-center">
        <CheckIcon
          className={clsx(
            'w-4 h-4 absolute -left-1',
            statusDefault ? 'text-gray-400' : 'text-blue'
          )}
        />
        <CheckIcon
          className={clsx(
            'w-4 h-4',
            statusDefault ? 'text-gray-400' : 'text-green'
          )}
        />
      </span>
    ) : statusFilter === '2' ? (
      <span className="relative inline-block w-4 h-4">
        <ShieldCheckIcon
          className={clsx(
            'w-3 h-3 absolute top-1 absolute left-1',
            statusDefault ? 'text-gray-200' : 'text-orange'
          )}
        />
        <CheckCircleIcon
          className={clsx(
            'w-3 h-3 absolute top-0 left--1',
            statusDefault ? 'text-gray-400' : 'text-green-400'
          )}
        />
      </span>
    ) : (
      <FunnelIcon
        className={clsx(
          'w-4 h-4',
          statusDefault ? 'text-gray-400' : 'text-purple-400'
        )}
      />
    );

  // Replies icon
  const repliesDefault = !hideReplies;
  const repliesIcon = hideReplies ? (
    <ChatBubbleOvalLeftIcon
      className={clsx(
        'w-4 h-4',
        repliesDefault ? 'text-gray-400' : 'text-white'
      )}
    />
  ) : (
    <ChatBubbleLeftRightIcon
      className={clsx(
        'w-4 h-4',
        repliesDefault ? 'text-gray-400' : 'text-white'
      )}
    />
  );

  // Sort icon
  const sortDefault = sortOrder === 'newest';
  const sortIcon =
    sortOrder === 'oldest' ? (
      <ArrowUpIcon
        className={clsx(
          'w-4 h-4',
          sortDefault ? 'text-gray-400' : 'text-purple-400'
        )}
      />
    ) : (
      <ArrowDownIcon
        className={clsx(
          'w-4 h-4',
          sortDefault ? 'text-gray-400' : 'text-purple-400'
        )}
      />
    );

  // AutoMod icon
  const autoModDefault = !autoMod;
  const autoModIcon = (
    <ShieldCheckIcon
      className={clsx(
        'w-4 h-4',
        autoModDefault ? 'text-gray-400' : 'text-white'
      )}
    />
  );

  // Button class
  const btnClass =
    'flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-white/20 transition';
  const activeBg = 'bg-purple-600/80';
  const defaultBg = 'bg-white/10';

  return (
    <div className="flex flex-wrap top-4 items-center gap-1 px-2 py-1 bg-grey rounded-lg">
      {/* Status */}
      <div className="relative">
        <button
          className={clsx(btnClass, statusDefault ? defaultBg : activeBg)}
        >
          {statusIcon}
          <span className="hidden sm:inline">Status</span>
          <ChevronDownIcon className="w-3 h-3 ml-1" />
        </button>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
        >
          <option value="all">All</option>
          <option value="0">New</option>
          <option value="1">Seen</option>
          <option value="2">Closed</option>
        </select>
      </div>

      {/* Replies */}
      <button
        className={clsx(btnClass, repliesDefault ? defaultBg : activeBg)}
        onClick={() => setHideReplies(!hideReplies)}
        title="Toggle hide replies"
      >
        {repliesIcon}
        <span className="hidden sm:inline">Replies</span>
      </button>

      {/* Sort */}
      <div className="relative">
        <button className={clsx(btnClass, sortDefault ? defaultBg : activeBg)}>
          {sortIcon}
          <span className="hidden sm:inline">Sort</span>
          <ChevronDownIcon className="w-3 h-3 ml-1" />
        </button>
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      {/* AutoMod */}
      <button
        className={clsx(
          btnClass,
          autoMod
            ? 'bg-orange hover:bg-orange/80'
            : autoModDefault
            ? defaultBg
            : activeBg
        )}
        onClick={() => setAutoMod(!autoMod)}
        title="Toggle AutoMod"
      >
        {autoModIcon}
        <span className="hidden sm:inline">Moderated</span>
      </button>

      {/* Search */}
      <div className="flex items-center bg-white/10 rounded px-2 py-1 ml-2">
        <MagnifyingGlassIcon className="w-4 h-4 text-white/70" />
        <input
          type="text"
          value={localSearchText}
          onChange={(e) => setLocalSearchText(e.target.value)}
          placeholder="Search"
          className="bg-transparent outline-none text-xs text-white px-2 w-20"
        />
        <select
          value={removal ? 'removal' : 'search'}
          onChange={(e) => setRemoval(e.target.value === 'removal')}
          className="bg-transparent text-xs text-white outline-none"
        >
          <option value="search">Search</option>
          <option value="removal">Removal</option>
        </select>
      </div>

      {/* Clear Filters */}
      <button
        className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-white/10 text-white/80 border border-white/5 backdrop-blur-sm hover:bg-white/15 hover:border-white/20 active:bg-white/5 transition-all duration-300 ease-in-out focus:outline-none ml-auto"
        onClick={() => {
          setSortOrder('newest');
          setStatusFilter('all');
          setHideReplies(false);
          setSearchText('');
          setLocalSearchText('');
          setRemoval(false);
          setAutoMod(false);
        }}
        title="Clear all filters"
      >
        <svg
          className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        <span className="relative overflow-hidden">
          Clear
          <span className="absolute bottom-0 left-0 w-full h-[1px] bg-white/40 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
        </span>
      </button>
    </div>
  );
};

export default FilterBar;
